import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarPlus,
  HandCoins,
  MapPin,
  Plus,
  ReceiptText,
  Settings,
  Sparkles,
} from "lucide-react";

import { formatDateRange, formatDestinations } from "@travelix/core";
import { useTrip } from "@/hooks/use-trip";
import { InlineAd } from "@/components/inline-ad";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { isLikelyBrokenCoverUrl } from "@/lib/cover-url";
import { isExpenseLoggingClosed, isTripReadOnly } from "@/lib/trip-readonly";
import { useTourState } from "@/lib/onboarding/tour-state";
import {
  TripActionBar,
  TRIP_ACTION_BAR_SPACER,
  type TripActiveTab,
} from "@/components/trip-action-bar";

/**
 * Trip detail layout —
 *   header (photo hero: back/settings + name/date/destination + FAB)
 *   ad slot
 *   tab content
 *   bottom tab bar
 *
 * No horizontal gestures live inside the content grid: rows are
 * tap-to-open, and destructive actions sit behind a ConfirmDialog on
 * the detail screen. Horizontal swipes belong to the OS (iOS edge-swipe
 * back) and to tab navigation, so rows must never capture them.
 */
export default function TripLayoutRoute() {
  const { id } = useParams<{ id: string }>();
  const { data: trip, isLoading, isError } = useTrip(id);
  const location = useLocation();
  const [imgBroken, setImgBroken] = useState(false);
  // No ads during the tutorial: the native AdMob banner draws above the
  // WebView and would cover the tour's popovers and hint bar.
  const { active: touring } = useTourState();

  const activeTab = useMemo<TripActiveTab>(() => {
    if (location.pathname.endsWith("/expenses")) return "expenses";
    if (location.pathname.endsWith("/plan")) return "plan";
    if (location.pathname.endsWith("/documents")) return "documents";
    return "timeline";
  }, [location.pathname]);

  if (isLoading) return <HeroSkeleton />;
  if (isError || !trip) return <ErrorHero />;

  const showImage =
    !!trip.coverImage && !imgBroken && !isLikelyBrokenCoverUrl(trip.coverImage);
  const readOnly = isTripReadOnly(trip);
  const expenseLoggingClosed = isExpenseLoggingClosed(trip);
  const destinationsLabel = trip.destinations.length
    ? formatDestinations(trip.destinations)
    : null;

  // Floating add CTA on the hero — target and label depend on the tab.
  // Plan and Documents don't have a canonical single add flow, so their
  // FAB is suppressed (Plan owns its own PlanActionBar). Expenses get a
  // grace period past readOnly (see isExpenseLoggingClosed) and opens a
  // choice (expense vs. payment) instead of a single Link — see
  // ExpenseFab below.
  const fab =
    activeTab === "timeline"
      ? readOnly
        ? null
        : { to: `/trip/${trip.id}/add-event`, label: "Add event", Icon: CalendarPlus }
      : null;

  return (
    <div className={cn("min-h-full bg-bg", TRIP_ACTION_BAR_SPACER)}>
      {/* ─── Header / hero ─── The section itself is overflow-visible
          so the FAB can escape the bottom edge; the image and its
          gradients are clipped by an inner wrapper. */}
      <section
        data-tour="trip-hero"
        className="relative mx-auto aspect-[10/6] w-full max-w-2xl"
        style={{ viewTransitionName: `trip-${trip.id}-image` }}
      >
        <div className="absolute inset-0 overflow-hidden">
          {/* Gradient stand-in for missing images */}
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(135deg,#E4572E,#7A2712)" }}
            aria-hidden
          />
          {showImage ? (
            <img
              src={trip.coverImage!}
              alt=""
              onError={() => setImgBroken(true)}
              className="absolute inset-0 h-full w-full object-cover ken-burns"
            />
          ) : null}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(20,15,10,0.78) 0%, rgba(20,15,10,0.1) 55%, rgba(20,15,10,0.35) 100%)",
            }}
            aria-hidden
          />
        </div>

        <nav className="absolute inset-x-3 top-3 z-10 flex items-center justify-between">
          <BackButton />
          <NavLink
            to={`/trip/${trip.id}/settings`}
            aria-label="Trip settings"
            className="flex h-11 w-11 items-center justify-center rounded-full text-white bg-black/35 backdrop-blur-md"
          >
            <Settings className="h-5 w-5" />
          </NavLink>
        </nav>

        <div className="absolute inset-x-5 bottom-4 flex flex-col gap-1 pr-16 text-white">
          <h1
            className="font-display leading-none tracking-tight text-white m-0"
            style={{
              fontVariationSettings: `"opsz" 60, "wght" 500`,
              fontSize: "clamp(24px, 7vw, 36px)",
              letterSpacing: "-0.02em",
            }}
          >
            {trip.name}
          </h1>
          <p className="text-[12px] font-medium text-white/85 tracking-wide m-0">
            {formatDateRange(trip.startDate, trip.endDate)}
          </p>
          {destinationsLabel ? (
            <p className="mt-0.5 inline-flex items-center gap-1.5 text-[12px] font-medium text-white/80 m-0">
              <MapPin className="h-3.5 w-3.5" strokeWidth={2.25} />
              {destinationsLabel}
            </p>
          ) : null}
        </div>

        {/* Floating add CTA — half-crosses the hero/content boundary.
            The section is overflow-visible so the pill isn't clipped;
            the image behind it stays clipped by the wrapper above.
            Expenses gets a choice (expense vs. payment) instead of a
            plain Link — see ExpenseFab. */}
        {activeTab === "expenses" ? (
          expenseLoggingClosed ? null : <ExpenseFab tripId={trip.id} />
        ) : fab ? (
          <Link
            to={fab.to}
            aria-label={fab.label}
            data-tour={activeTab === "timeline" ? "add-event" : undefined}
            className={cn(
              "absolute right-5 -bottom-6 z-20",
              "flex h-12 items-center gap-2 rounded-full pl-3.5 pr-4",
              "bg-primary text-white shadow-warm-3",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
            )}
          >
            <fab.Icon className="h-5 w-5 shrink-0" strokeWidth={2.5} />
            <span className="text-[13px] font-semibold whitespace-nowrap">{fab.label}</span>
          </Link>
        ) : null}
      </section>

      {/* ─── Ad slot ─── In the content flow under the hero, so the
          native banner scrolls with the page like any other row. */}
      {touring ? null : (
        <div className="mt-8 mx-auto max-w-2xl px-5">
          <InlineAd slot="trip" />
        </div>
      )}

      {readOnly ? <RecapBanner tripId={trip.id} /> : null}

      {/* ─── Content ─── */}
      <div role="tabpanel" aria-labelledby={`trip-tab-${activeTab}`} className="pt-1">
        <Outlet />
      </div>

      {/* ─── Bottom tab bar ─── */}
      <TripActionBar tripId={trip.id} activeTab={activeTab} readOnly={readOnly} />
    </div>
  );
}

// ─── Expenses FAB — choice between an expense and a payment ────
/**
 * Replaces a plain "Add expense" Link with a small choice menu, since
 * expenses and payments are two different things people track here
 * (money spent vs. money settled between members) and forcing every
 * entry through the expense form was the reason payments got faked as
 * "misc" expenses in the first place.
 */
function ExpenseFab({ tripId }: { tripId: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} data-tour="expense-fab" className="absolute right-5 -bottom-6 z-20">
      {open ? (
        <div
          role="menu"
          aria-label="Add expense or payment"
          className={cn(
            "absolute bottom-full right-0 mb-2 w-52 origin-bottom-right rounded-xl",
            "border border-line bg-bg-elevated shadow-warm-3 p-1.5",
            "animate-in fade-in slide-in-from-bottom-1 duration-150",
          )}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate(`/trip/${tripId}/add-expense`);
            }}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[14px] font-medium text-ink hover:bg-bg-sunken"
          >
            <ReceiptText className="h-4 w-4 text-ink-subtle" />
            Add expense
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate(`/trip/${tripId}/record-payment`);
            }}
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-[14px] font-medium text-ink hover:bg-bg-sunken"
          >
            <HandCoins className="h-4 w-4 text-ink-subtle" />
            Record payment
          </button>
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Add expense or record payment"
        className={cn(
          "flex h-12 items-center gap-2 rounded-full pl-3.5 pr-4",
          "bg-primary text-white shadow-warm-3",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        )}
      >
        <Plus className="h-5 w-5 shrink-0" strokeWidth={2.5} />
        <span className="text-[13px] font-semibold whitespace-nowrap">Add</span>
      </button>
    </div>
  );
}

// ─── Recap banner (only on past trips) ─────────────────────────
function RecapBanner({ tripId }: { tripId: string }) {
  return (
    <div className="mx-auto max-w-2xl px-5 pt-3">
      <NavLink
        to={`/trip/${tripId}/recap`}
        className={cn(
          "flex items-center gap-3 rounded-xl border border-primary/30 bg-primary-soft/40 px-4 py-3",
          "hover:bg-primary-soft/60",
        )}
      >
        <Sparkles className="h-4 w-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[14px] font-semibold text-ink m-0">Your trip is over</p>
          <p className="text-[12px] text-ink-muted m-0">
            Mostly read-only now — see the recap for the highlights.
          </p>
        </div>
        <span className="text-[12px] font-semibold text-primary">Open</span>
      </NavLink>
    </div>
  );
}

function BackButton() {
  return (
    <NavLink
      to="/dashboard"
      aria-label="Back to trips"
      className="flex h-11 w-11 items-center justify-center rounded-full text-white bg-black/35 backdrop-blur-md"
    >
      <ArrowLeft className="h-5 w-5" />
    </NavLink>
  );
}

function HeroSkeleton() {
  return (
    <div className="min-h-full bg-bg">
      <Skeleton className="mx-auto aspect-[10/6] w-full max-w-2xl rounded-none" />
      <div className="mt-8 mx-auto h-16 max-w-2xl px-5">
        <Skeleton className="h-full w-full rounded-xl" />
      </div>
    </div>
  );
}

function ErrorHero() {
  return (
    <div className="flex min-h-full items-center justify-center bg-bg px-6 text-center">
      <div>
        <p className="font-display text-2xl text-ink" style={{ fontVariationSettings: `"opsz" 48, "wght" 500` }}>
          Trip not found
        </p>
        <p className="mt-1 text-[14px] text-ink-muted">
          It might have been deleted, or the link is off.
        </p>
        <NavLink to="/dashboard" className="mt-4 inline-block text-primary font-semibold hover:underline">
          Back to your trips
        </NavLink>
      </div>
    </div>
  );
}

